import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";

const PAY_KEYS = ["contact_email"];
const inputCls = "mt-2 w-full rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] px-4 py-3 text-sm outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--tint-pink)]";

export const AdminSettingsPage = () => {
  const [heroImage, setHeroImage] = useState("");
  const [aboutImage, setAboutImage] = useState("");
  const [aboutText, setAboutText] = useState("");
  const [pay, setPay] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/site-settings")
      .then((r) => {
        setHeroImage(r.data.hero_image || "");
        setAboutImage(r.data.about_image || "");
        setAboutText(r.data.about_text || "");
        setPay(Object.fromEntries(PAY_KEYS.map((k) => [k, r.data[k] || ""])));
      })
      .catch((e) => toast.error(formatApiError(e, "Nu am putut încărca setările")))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site-settings", { hero_image: heroImage, about_image: aboutImage, about_text: aboutText, contact_email: pay.contact_email || "" });
      toast.success("Setările au fost salvate");
    } catch (err) {
      toast.error(formatApiError(err, "Nu am putut salva"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="py-24 text-center text-sm text-[#F7F1E8]/68" data-testid="settings-loading">Se încarcă…</p>;
  }

  return (
    <main data-testid="admin-settings" className="mx-auto max-w-3xl px-6 py-12">
      <Link
        to="/admin"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78 hover:text-[#F7F1E8]"
      >
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.6} /> Înapoi la Atelier
      </Link>

      <div className="mt-6 border-b border-[color-mix(in_srgb,var(--milk)_18%,transparent)] pb-6">
        <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)]">Setări</p>
        <h1 className="mt-2 font-serif text-4xl font-light text-[#F7F1E8]">Setările site-ului</h1>
        <p className="mt-3 text-sm text-[#F7F1E8]/78">
          Aici schimbi pozele site-ului și textul de prezentare din pagina Despre.
        </p>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-10 md:grid-cols-2">
        <div>
          <ImageUploader
            testId="settings-hero-upload"
            label="Portret prima pagină (hero)"
            aspect="aspect-[297/210]"
            value={heroImage}
            onChange={setHeroImage}
          />
          <p className="mt-2 text-[11px] text-[#F7F1E8]/68">
            Apare ca o pagină A4 așezată pe orizontală (peisaj). Alege o fotografie orizontală.
          </p>
        </div>
        <div>
          <ImageUploader
            testId="settings-about-upload"
            label="Fotografie pagina Despre"
            aspect="aspect-square"
            value={aboutImage}
            onChange={setAboutImage}
          />
          <p className="mt-2 text-[11px] text-[#F7F1E8]/68">
            Apare în format pătrat, lângă textul de prezentare.
          </p>
        </div>
      </div>

      <div className="mt-10">
        <label className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">Textul din pagina Despre</label>
        <textarea
          data-testid="settings-about-text"
          value={aboutText}
          onChange={(e) => setAboutText(e.target.value)}
          rows={10}
          maxLength={8000}
          className="mt-2 w-full rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] px-4 py-3 text-sm leading-relaxed outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--tint-pink)]"
          placeholder="Scrie aici textul de prezentare…"
        />
        <p className="mt-2 text-[11px] text-[#F7F1E8]/68">
          Separă paragrafele cu un rând gol. Textul apare exact așa în pagina Despre.
        </p>
      </div>

      <div className="mt-12 border-t border-[color-mix(in_srgb,var(--milk)_18%,transparent)] pt-10" data-testid="settings-contact">
        <p className="text-xs uppercase tracking-[0.3em] text-[var(--teal-dark)]">Hai la o cafea · Întrebări anonime</p>
        <h2 className="mt-2 font-serif text-2xl text-[#F7F1E8]">Unde primești mesajele</h2>
        <p className="mt-2 text-sm text-[#F7F1E8]/78">
          Fiecare mesaj privat, fiecare întrebare anonimă și o copie a newsletterului de vineri ajung pe acest e-mail (și rămân și în Atelier). Dacă e gol, folosim alexia.enache.2002@gmail.com.
        </p>
        <input
          data-testid="settings-contact-email"
          type="email"
          value={pay.contact_email || ""}
          onChange={(e) => setPay((p) => ({ ...p, contact_email: e.target.value }))}
          placeholder="alexia.enache.2002@gmail.com"
          className={`${inputCls} md:w-1/2`}
        />
      </div>

      <div className="mt-10 flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          data-testid="settings-save-btn"
          className="inline-flex items-center gap-2 rounded-full bg-[var(--accent)] px-6 py-3 text-xs font-medium uppercase tracking-[0.2em] text-white transition-transform hover:-translate-y-0.5 active:scale-95 disabled:opacity-60"
        >
          <Save className="h-3.5 w-3.5" strokeWidth={1.6} />
          {saving ? "Se salvează…" : "Salvează modificările"}
        </button>
        <Link
          to="/"
          className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/68 hover:text-[#F7F1E8]"
          target="_blank"
          rel="noopener noreferrer"
        >
          Vezi pe site →
        </Link>
      </div>
    </main>
  );
};

export default AdminSettingsPage;
